import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { getAuthIsLoggedIn } from '@rd/core/auth/store/selectors/auth.selectors';
import { UnleashFacade } from '@rd/core/facades/unleash-facade/unleash-facade.ts';
import { getLocaleSelected } from '@rd/core/locale/store/selectors/locale.selectors';
import { getLoyaltyCurrencySelected } from '@rd/core/loyalty-currency/store/selectors/loyalty-currency.selectors';
import { AnalyticsService } from '@rd/core/services/analytics/analytics.service';
import { ScrollRestorationService } from '@rd/core/services/scroll-restoration/scroll-restoration.service';
import { isNotNull } from '@rd/utils/isNotNull';
import { combineLatest } from 'rxjs';
import {
  delay,
  distinctUntilChanged,
  filter,
  map,
  take,
  tap,
} from 'rxjs/operators';
import { AppInit, AppReady } from './app-init.action';

@Injectable()
export class AppInitEffects {
  constructor(
    private actions$: Actions,
    private analyticsService: AnalyticsService,
    private router: Router,
    private scrollRestorationService: ScrollRestorationService,
    private store: Store,
    private unleashFacade: UnleashFacade,
  ) {}

  isReady$ = combineLatest([
    this.store
      .select(getLocaleSelected)
      .pipe(filter(isNotNull), delay(0), distinctUntilChanged()),
    this.store.select(getLoyaltyCurrencySelected).pipe(filter(isNotNull)),
    this.unleashFacade.isUnleashClientReady$,
  ]);

  initAppReady$ = createEffect(() => this.isReady$.pipe(map(() => AppReady())));

  initializeServiceOnReady$ = createEffect(
    () =>
      this.actions$.pipe(ofType(AppReady)).pipe(
        tap(() => {
          this.analyticsService.injectAnalyticsScript();
        }),
      ),
    {
      dispatch: false,
    },
  );

  initializeServiceOnInit$ = createEffect(
    () =>
      this.actions$.pipe(ofType(AppInit)).pipe(
        tap(() => {
          this.scrollRestorationService.scrollRestoration();
        }),
      ),
    {
      dispatch: false,
    },
  );

  initNavigation$ = createEffect(
    () =>
      this.store.select(getAuthIsLoggedIn).pipe(
        filter(isNotNull),
        tap(() => {
          // Only start router navigation after either internal login/logout happens, so that the AuthGuard
          // gets the most updated isLoggedIn value from the reducer when the route is about to be activated.
          this.router.initialNavigation();
        }),
        take(1),
      ),
    {
      dispatch: false,
    },
  );

  logCurrentVersion$ = createEffect(
    () =>
      this.actions$.pipe(ofType(AppInit)).pipe(
        tap(() => {
          if (window.rd_version) {
            console.log('\u001b[1;35m Deploy version', window.rd_version);
          }
        }),
      ),
    {
      dispatch: false,
    },
  );
}
